import { generateName } from '@common/Common';
import PortfolioNavigation from "@common/Components/PortfolioNavigation.vue";
import { guid } from '@common/Guid';
import { ValidationError } from '@common/types';
import { ICommandStatus } from '@cqrs/cqrs';
import { ICriteriaCommand } from '@Criteria/cqrs';
import { CriterionSelectorDialogViewModel } from '@Criteria/Hierarchies/Components/CriterionSelectorDialog';
import CriterionSelectorDialog from '@Criteria/Hierarchies/Components/CriterionSelectorDialog.vue';
import { CriterionReadModel } from '@Criteria/Hierarchies/Domain/readmodels';
import { CreateWeightedScoreCommandV1 } from '@Criteria/WeightedScores/Domain/commands-core';
import WeightedScoresCommands from "@Criteria/WeightedScores/Domain/commands-vue-component";
import WeightedScoresQueries from '@Criteria/WeightedScores/Domain/queries-vue-component';
import { WeightedScoreSettingsReadModel } from '@Criteria/WeightedScores/Domain/readmodels';
import { WeightsSetReadModel } from '@Criteria/Weights/Domain/readmodels';
import Component, { mixins } from 'vue-class-component';
import { Route } from 'vue-router';

@Component({
    components: { PortfolioNavigation, CriterionSelectorDialog },
})
export default class EditWeightedScorePage extends mixins(WeightedScoresQueries, WeightedScoresCommands)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId() { return Number.parseInt(this.$route.params.projectId); }

    criterionSelectorDialog = new CriterionSelectorDialogViewModel();

    rm: WeightedScoreSettingsReadModel | null = null;
    initialized: boolean = false;

    name: string | null = null;
    description: string | null = null;
    weightsSetId: number | null = null;
    criterionId: number | null = null;
    errors: ValidationError[] = [];
    isSaving: boolean = false;

    get evalModelId(): number { return this.rm.EvalModelId; }
    get criteriaHierarchyId(): number { return this.rm.CriteriaHierarchyId; }
    
    get attributeId(): number { return this.rm.AttributeId; }
    get criteria(): CriterionReadModel[] { return this.rm.Criteria ?? []; }
    get weightsSets(): WeightsSetReadModel[] { return this.rm.WeightsSets ?? []; }
    get goalCriterionId(): number { return this.criteria.find(c => c.ParentId == null).CriterionId; }
    get criterionLabel(): string | null
    {
        if (this.criterionId == null)
        {
            return this.criteria.find(a => a.ParentId == null)?.Name ?? null;
        }

        return this.criteria.find(a => a.CriterionId === this.criterionId)?.Name ?? null;
    }
    get nameError(): string | null { return this.errors.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null; }
    get hasChanges(): boolean
    {
        return this.name !== this.rm.Name ||
            this.description !== this.rm.Description ||
            this.criterionId !== this.rm.CriterionId ||
            this.weightsSetId !== this.rm.WeightsSetId;
    }
    get saveButtonText(): string { return this.isSaving ? "Saving..." : "Create"; }


    handleShowCriterionSelector()
    {
        this.criterionSelectorDialog.show(this.criterionId, this.criteria);
    }

    handleSetCriterion(criterionId: number)
    {
        if (this.goalCriterionId === criterionId)
        {
            this.criterionId = null;
        }
        else
        {
            this.criterionId = criterionId;
        }
    }

    handleCancel()
    {
        this.$router.go(-1);
    }

    handleBack()
    {
        this.$router.go(-1);
    }

    handleSave()
    {
        this.isSaving = true;
        this.errors = [];
        this.invokeCreateWeightedScoreCommandV1(this.workspaceId, guid(), this.criteriaHierarchyId, this.weightsSetId, this.criterionId, this.name, this.description);
    }


    mounted()
    {
        document.title = "Create weighted score";

        if (this.$route.query.attributeId)
        {
            this.runWeightedScoreSettingsByAttributeIdQueryV1(this.workspaceId, this.projectId, Number.parseInt(this.$route.query.attributeId as string));
        }
        else
        {
            this.runBlankWeightedScoreSettingsQueryV1(this.workspaceId, this.projectId, Number.parseInt(this.$route.params.evalModelId));
        }
    }

    onSetModel(model: any, modelName: string)
    {
        if (modelName === "WeightedScoreSettingsReadModel")
        {
            this.rm = model as WeightedScoreSettingsReadModel;

            this.name = generateName(this.rm.Name ?? "Score");
            this.description = this.rm.Description;
            this.weightsSetId = this.rm.WeightsSetId;
            this.criterionId = this.rm.CriterionId;

            this.initialized = true;
        }
    }

    onCommandSucceed(cmd: ICriteriaCommand, correlationId: string, result: any)
    {
        if (CreateWeightedScoreCommandV1.matchType(cmd))
        {
            this.$router.go(-1);
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (CreateWeightedScoreCommandV1.matchType(status.Command))
        {
            this.errors = status.ValidationErrors;
            this.isSaving = false;
        }
    }

    beforeRouteLeave(to: Route, from: Route, next: (arg?: any) => void) 
    {
        if (this.hasChanges === true && this.isSaving === false) 
        {
            if (confirm("You have unsaved changes. Do you want to save them before leaving?")) 
            {
                this.handleSave();
                next();
            }
            else 
            {
                next();
            }
        }
        else 
        {
            next();
        }
    }
}
